import { Component, OnInit, ViewChild } from '@angular/core';
import { NgxSmartModalComponent, NgxSmartModalService } from 'ngx-smart-modal';
import { TranslateService } from '@ngx-translate/core';
import { User, UserRole, UserService } from '../../core/user/user.service';
import { PageService } from '../../core/page.service';
import { FormControl, Validators } from '@angular/forms';
import { UsersService } from './users.service';
import { Breakpoint, fromMediaQuery } from '../../core/helpers/utils';
import { SelectOption } from '../../shared/select-input/select-input.component';
import { AnalyticsEvent, AnalyticsPage, InternalAnalyticsService } from '../../core/internal-analytics.service';

@Component({
    selector: 'rosie-users',
    templateUrl: './users.component.html',
    styleUrls: ['./users.component.scss'],
    standalone: false
})
export class UsersComponent implements OnInit {
  roleOptions: SelectOption[] = [{ value: UserRole.Admin, label: 'Admin', description: 'Admin users have full access to everything.' }];
  temporaryRole: FormControl = new FormControl(UserRole.Admin, [Validators.required]);
  temporaryUser: FormControl = new FormControl('', [Validators.required, Validators.email]);
  editedItemIndex: number;
  removeActionInProgress = false;
  isMobileVersion = false;
  inProgress = false;
  resendInProgress = '';
  errorMessage: string;
  @ViewChild('userModal') userModal: NgxSmartModalComponent;

  constructor(private users: UsersService, private translate: TranslateService, private page: PageService, private modal: NgxSmartModalService, private user: UserService, private analytics: InternalAnalyticsService) {
    this.page.initializePage(`Rosie - Users`, AnalyticsPage.AccountUsers);
  }

  get businessUsers(): User[] {
    return this.users.businessUsers;
  }

  get isEditingExistingUser(): boolean {
    return this.editedItemIndex != null;
  }

  get isEditingAccountOwner(): boolean {
    return this.businessUsers[this.editedItemIndex]?.role?.title === UserRole.Owner;
  }

  isActiveUser(index?: number): boolean {
    const selectedIndex = index != null ? index : this.editedItemIndex;
    return !!(!this.businessUsers[selectedIndex]?.inviteId && this.businessUsers[selectedIndex]?.id);
  }

  ngOnInit(): void {
    fromMediaQuery(Breakpoint.UpToMedium).subscribe(matches => this.isMobileVersion = matches);
  }

  getTranslation(translationKey: string): string {
    return this.translate.instant(`ACCOUNT.TABS.${translationKey?.toUpperCase()}`);
  }

  isTheSameUser() {
    return this.temporaryUser.value === this.user.email;
  }

  resendInvite(inviteId: string) {
    this.resendInProgress = inviteId;
    this.users.resendInvite(inviteId).subscribe(() => {
      this.resendInProgress = '';
    }, () => this.resendInProgress = '');
  }

  handleUser(index?: number) {
    if (index != null) {
      this.editedItemIndex = index;
      this.temporaryUser.patchValue(this.businessUsers[index]?.email);
    } else {
      this.editedItemIndex = null;
      this.temporaryUser.reset();
    }

    this.userModal.open();
  }

  handleModalClose() {
    this.editedItemIndex = null;
    this.removeActionInProgress = false;
    this.errorMessage = null;
    this.temporaryUser.reset();
    this.modal.closeAll();
  }

  handleItemRemoveAction() {
    this.removeActionInProgress = true;
  }

  handleItemRemoveCancel() {
    this.removeActionInProgress = false;
  }

  letUsKnow() {
    const el: any = document.querySelector('.roles-intercom-trigger');
    if (el) {
      el.addEventListener('click', () => { console.log('Users let us know clicked'); });
      el.click();
    }
  }

  save() {
    if (!this.isActiveUser(this.editedItemIndex)) {
      this.inProgress = true;
      this.users.addUser(this.temporaryUser.value)
        .subscribe((user) => {
          this.inProgress = false;
          this.temporaryUser.reset();
          this.users.businessUsers.push(user);
          this.modal.closeAll();
          this.analytics.trackEvent(AnalyticsEvent.UserInvited);
        }, (error) => {
          this.inProgress = false;
          this.errorMessage = error;
        });
    } else {
      this.modal.closeAll();
    }
  }

  contactSupport() {
    const el: any = document.querySelector('.general-intercom-trigger-help');
    if (el) {
      el.addEventListener('click', () => { console.log('Contact support clicked'); });
      el.click();
    }
  }

  removeItem() {
    const editedUser = this.businessUsers[this.editedItemIndex];
    const request = this.isActiveUser() ? this.users.removeUser(editedUser.id) : this.users.removeInactiveUser(editedUser.inviteId);
    request.subscribe();
    this.businessUsers.splice(this.editedItemIndex, 1);
    this.modal.closeAll();
    this.analytics.trackEvent(AnalyticsEvent.UserRemoved);
  }
}

